<template>
	<form class="job-create">
		<h2 class="text-center mb-2">Create your job</h2>
		<div class="job-create-form-group">
			<BaseInput
				label="Booking Number"
				v-model="job.booking_number"
				class="job-create-form-group-left"
				:errorMsg="validationErrors.booking_number"
			/>
			<BaseSelect
				label="Job Type"
				class="job-create-form-group-right"
				:options="jobType"
				v-model="job.job_type"
				:errorMsg="validationErrors.job_type"
			/>
		</div>
		<BaseInput label="Job Title" v-model="job.job_title" :errorMsg="validationErrors.job_title"/>
		<BaseInput
			label="Publication Name"
			v-model="job.publication_name"
			:errorMsg="validationErrors.publication_name"
		/>
		<div class="job-create-form-group">
			<BaseSelect
				v-model="job.publication_type"
				label="Publication type"
				class="job-create-form-group-left"
				:options="publicationType"
				:errorMsg="validationErrors.publication_type"
			/>
			<BaseSelect
				label="Colour Mode"
				class="job-create-form-group-right"
				:options="colourMode"
				v-model="job.colour_mode"
				:errorMsg="validationErrors.colour_mode"
			/>
		</div>
		<div class="job-create-form-group">
			<BaseInput
				label="Print Width(mm)"
				class="job-create-form-group-left"
				v-model="job.print_width"
				:errorMsg="validationErrors.print_width"
			/>
			<BaseInput
				label="Print Depth(mm)"
				class="job-create-form-group-right"
				v-model="job.print_depth"
				:errorMsg="validationErrors.print_depth"
			/>
		</div>
		<div class="job-create-form-group">
			<BaseInput
				label="Width Incl. Bleed (mm)"
				class="job-create-form-group-left"
				v-model="job.bleed_width"
				:errorMsg="validationErrors.bleed_width"
			/>
			<BaseInput
				label="Depth Incl. Bleed (mm)"
				class="job-create-form-group-right"
				v-model="job.bleed_depth"
				:errorMsg="validationErrors.bleed_depth"
			/>
		</div>
		<div class="job-create-form-group">
			<BaseInput
				label="Web Width(mm)"
				class="job-create-form-group-left"
				v-model="job.web_width"
				:errorMsg="validationErrors.web_width"
			/>
			<BaseInput
				label="Web Depth(mm)"
				class="job-create-form-group-right"
				v-model="job.web_depth"
				:errorMsg="validationErrors.web_depth"
			/>
		</div>
		<label>Run Date</label>
		<datepicker
			placeholder="Select a date"
			inputClass="datepicker-input"
			v-model="job.run_date"
			:format="customFormatter"
			:disabledDates="disabledDates"
			@closed="closeRunDateDatePicker"
		/>
		<p class="errorMsg" v-if="validationErrors.deadline">{{ validationErrors.run_date }}</p>
		<label>Deadline</label>
		<datepicker
			placeholder="Select a date"
			inputClass="datepicker-input"
			v-model="job.deadline"
			:format="customFormatter"
			:disabledDates="disabledDates"
			@closed="closeDeadlineDatePicker"
		/>
		<p class="errorMsg" v-if="validationErrors.deadline">{{ validationErrors.deadline }}</p>
		<BaseTextarea
			label="Instructions"
			rows="5"
			v-model="job.instructions"
			:errorMsg="validationErrors.instructions"
		/>
		<BaseAttachment :attachments="job.attachments" @input="job.attachments = $event"/>
		<label>Upload images, documents or other ad component files</label>
		<BaseFileInput
			:attachments="job.attachments"
			@input="job.attachments = $event"
			:attachmentId="attachmentId"
			@add="addAttachmentId"
		/>

		<div class="text-right">
			<mdb-btn @click.native.prevent="createJob" class="btn-color-primary">Create Job</mdb-btn>
		</div>
	</form>
</template>

<script>
import { mdbBtn } from 'mdbvue'
import Datepicker from 'vuejs-datepicker'
import * as yup from 'yup'
import moment from 'moment'
import { getValidationErrors } from '@/utils/Help.js'
import NProgress from 'nprogress'

const schema = yup.object().shape({
	booking_number: yup
		.string()
		.label('Booking Number')
		.max(100),
	job_type: yup.string().label('Job Type'),
	job_title: yup
		.string()
		.max(50)
		.label('Job Title')
		.required("Job title can't be empty."),
	publication_name: yup
		.string()
		.max(50)
		.label('Publication Name'),
	publication_type: yup.string().label('Publication Type'),
	colour_mode: yup.string().label('Colour Mode'),
	print_width: yup
		.number()
		.typeError('Print Width must be a number')
		.min(0)
		.max(10000)
		.label('Print Width'),
	print_depth: yup
		.number()
		.typeError('Print Depth must be a number')
		.min(0)
		.max(10000)
		.label('Print Depth'),
	bleed_width: yup
		.number()
		.typeError('Bleed Width must be a number')
		.min(0)
		.max(10000)
		.label('Bleed Width'),
	bleed_depth: yup
		.number()
		.typeError('Bleed Depth must be a number')
		.min(0)
		.max(10000)
		.label('Bleed Depth'),
	web_width: yup
		.number()
		.typeError('Web Width must be a number')
		.min(0)
		.max(10000)
		.label('Web Width'),
	web_depth: yup
		.number()
		.typeError('Web Depth must be a number')
		.min(0)
		.max(10000)
		.label('Web Depth'),
	run_date: yup.string().label('Run Date'),
	deadline: yup.string().label('Deadline'),
	instructions: yup
		.string()
		.max(5000)
		.label('Instructions')
})

export default {
	name: 'JobCreate',
	data() {
		return {
			job: this.createFreshJobObject(),
			validationErrors: {},
			publicationType: [
				'Newspaper',
				'Magazine',
				'Web',
				'Flyer',
				'Brochure',
				'Catalogue',
				'Label',
				'Packaging',
				'Other'
			],
			colourMode: ['CMYK', 'Monochrome', 'Spot Colour', 'RGB', 'Other'],
			jobType: ['New', 'Repeat', 'Repeat Previous w/Alts'],
			disabledDates: {
				to: new Date(Date.now() - 8640000)
			},
			attachmentId: 1
		}
	},
	components: {
		mdbBtn,
		Datepicker
	},
	methods: {
		customFormatter(date) {
			return moment(date).format('D MMMM YYYY')
		},
		closeCustomFormatter(date) {
			return moment(date).format()
		},
		closeRunDateDatePicker() {
			this.job.run_date = this.closeCustomFormatter(this.job.run_date)
		},
		closeDeadlineDatePicker() {
			this.job.deadline = this.closeCustomFormatter(this.job.deadline)
		},
		createFreshJobObject() {
			const user = JSON.parse(sessionStorage.getItem('user'))

			const client_email = user.email
			const client_name = user.name
			const company_name = user.companyName
			const contact_number = user.contactNumber
			let freshJobObject = {
				job_type: 'New',
				job_title: '',
				publication_type: 'Newspaper',
				colour_mode: 'CMYK',
				create_time_stamp: '',
				client_name,
				client_email,
				company_name,
				contact_number,
				status: 'new',
				run_date: 'none',
				deadline: 'none',
				attachments: []
			}
			return freshJobObject
		},
		async createJob() {
			NProgress.start()
			this.job.create_time_stamp = moment()
				.utcOffset(600)
				.format()
			const {
				booking_number,
				job_type,
				job_title,
				publication_name,
				publication_type,
				colour_mode,
				print_width,
				print_depth,
				bleed_width,
				bleed_depth,
				web_width,
				web_depth,
				run_date,
				deadline,
				instructions
			} = this.job
			const userInput = {
				booking_number,
				job_type,
				job_title,
				publication_name,
				publication_type,
				colour_mode,
				print_width,
				print_depth,
				bleed_width,
				bleed_depth,
				web_width,
				web_depth,
				run_date,
				deadline,
				instructions
			}

			try {
				await schema.validate(userInput, {
					abortEarly: false
				})
				const job = this.job
				console.log(job)
				await this.$store.dispatch('job/createJob', {
					job
				})
				NProgress.done()
				this.$router.push({
					name: 'success'
				})
			} catch (error) {
				this.validationErrors = getValidationErrors(error)
				window.scrollTo(0, 0)
				NProgress.done()
			}
		},
		addAttachmentId() {
			this.attachmentId += 1
		}
	}
}
</script>

<style lang="scss" scoped>
@import '../styles/views/jobCreate.scss';
</style>
